/**
 * CompositeSignalBadge.jsx
 *
 * The single-verdict aggregator badge that lives in the Liquidity Map
 * header (next to the LiqScore pill). It reads the outputs of every
 * indicator already computed elsewhere, feeds them into the pure
 * compositeSignal module, and renders:
 *   • A colored pill "STRONG BUY | BUY | NEUTRAL | SELL | STRONG SELL"
 *     with a subtle glow matching the verdict.
 *   • The signed score (−100..+100) next to the label.
 *   • A preset picker (Conservador / Balanceado / Agresivo). Custom is
 *     PRO-gated and out-of-scope for Phase 1a — the picker shows the
 *     option but flips users to a "PRO only" tooltip when selected.
 *   • A tooltip/popover that breaks down which contributors are active
 *     and how much each one weighed. Trust > opaqueness.
 *
 * Free-tier delay: non-PRO users see a snapshot that ticks every 5 min.
 * PRO users get live updates (recomputed whenever inputs change).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { computeCompositeSignal, PRESETS, VERDICT_STYLE } from "./compositeSignal";
import { tLm } from "../../i18n/liqmap";

const FREE_SNAPSHOT_MS = 5 * 60 * 1000; // 5-minute delay per user directive
const STORAGE_KEY = "rebel_composite_preset";

/**
 * @param {{
 *   inputs: object,          // passed straight to computeCompositeSignal
 *   lang?: string,
 *   isPro?: boolean,
 * }} props
 */
export default function CompositeSignalBadge({ inputs, lang = "es", isPro = false }) {
  const t = tLm(lang);
  const [preset, setPreset] = useState(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      return v && PRESETS[v] ? v : "balanceado";
    } catch { return "balanceado"; }
  });
  const [tooltipOpen, setTooltipOpen] = useState(false);

  // ── Live vs delayed snapshot ────────────────────────────────────────
  // PRO users: recompute every time inputs change.
  // Free users: hold the previous snapshot for 5 minutes, then refresh.
  const [snapshot, setSnapshot] = useState(() => computeCompositeSignal(inputs, { preset }));
  const lastSnapAt = useRef(Date.now());

  // Recompute on inputs/preset change — but for free users only when
  // enough real time has elapsed (5 min).
  useEffect(() => {
    if (isPro) {
      setSnapshot(computeCompositeSignal(inputs, { preset }));
      lastSnapAt.current = Date.now();
      return;
    }
    // Free path: hold the previous snapshot for FREE_SNAPSHOT_MS unless
    //   (a) the user swapped preset (that's intent, always immediate), or
    //   (b) we currently show 0 active contributors and the new inputs
    //       would produce ≥1 — releasing NEUTRAL 0 immediately when real
    //       signals become available is worth more than strict 5-min
    //       cadence discipline (users otherwise see a stale zero for up
    //       to 5 minutes after the initial mount).
    setSnapshot((prev) => {
      const next = computeCompositeSignal(inputs, { preset });
      const preset_changed = !prev || prev.preset !== preset;
      const time_up = (Date.now() - lastSnapAt.current) >= FREE_SNAPSHOT_MS;
      const zero_to_signals =
        prev && prev.activeCount === 0 && next.activeCount > 0;
      if (preset_changed || time_up || zero_to_signals) {
        lastSnapAt.current = Date.now();
        return next;
      }
      return prev;
    });
    // Set up an interval that will refresh once the window elapses.
    const remaining = Math.max(1000, FREE_SNAPSHOT_MS - (Date.now() - lastSnapAt.current));
    const id = setTimeout(() => {
      lastSnapAt.current = Date.now();
      setSnapshot(computeCompositeSignal(inputs, { preset }));
    }, remaining);
    return () => clearTimeout(id);
  }, [inputs, preset, isPro]);

  const style = VERDICT_STYLE[snapshot.verdict] || VERDICT_STYLE.NEUTRAL;
  const verdictLabel = t.composite?.verdicts?.[snapshot.verdict] || style.label;

  const onSelectPreset = (p) => {
    setPreset(p);
    try { localStorage.setItem(STORAGE_KEY, p); } catch { /* ignore */ }
  };

  return (
    <div
      data-testid="composite-signal-badge"
      className="relative inline-flex items-center gap-1 font-mono"
      onMouseEnter={() => setTooltipOpen(true)}
      onMouseLeave={() => setTooltipOpen(false)}
      onFocus={() => setTooltipOpen(true)}
      onBlur={() => setTooltipOpen(false)}
      tabIndex={0}
      aria-describedby="composite-signal-tooltip"
    >
      {/* Verdict pill */}
      <div
        className="rounded-md px-2 py-[3px]"
        style={{
          background: `${style.color}1A`,
          border: `1px solid ${style.color}80`,
          color: style.color,
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: 1,
          boxShadow: `0 0 12px ${style.glow}`,
        }}
        data-testid="composite-verdict"
      >
        {verdictLabel}
        <span className="ml-2" style={{ color: "rgba(255,255,255,0.65)", fontWeight: 600 }}>
          {snapshot.score > 0 ? `+${snapshot.score}` : snapshot.score}
        </span>
      </div>

      {/* Preset picker — mini segmented buttons */}
      <div className="flex items-center gap-[2px] rounded-md border border-white/10 bg-black/40 px-[3px] py-[2px]">
        {["conservador", "balanceado", "agresivo"].map((p) => {
          const active = preset === p;
          const label = t.composite?.presets?.[p]
            || (p === "conservador" ? "Cons" : p === "balanceado" ? "Bal" : "Agr");
          return (
            <button
              key={p}
              type="button"
              onClick={() => onSelectPreset(p)}
              data-testid={`composite-preset-${p}`}
              aria-pressed={active}
              className="rounded px-1.5 py-[1px]"
              style={{
                fontSize: 8,
                letterSpacing: 1,
                fontWeight: 700,
                background: active ? "rgba(245,158,11,0.15)" : "transparent",
                color: active ? "#f59e0b" : "rgba(255,255,255,0.5)",
                cursor: "pointer",
              }}
              title={t.composite?.preset_titles?.[p] || ""}
            >
              {label.slice(0, 4).toUpperCase()}
            </button>
          );
        })}
        <button
          type="button"
          disabled={!isPro}
          data-testid="composite-preset-custom"
          className="rounded px-1.5 py-[1px]"
          style={{
            fontSize: 8, letterSpacing: 1, fontWeight: 700,
            background: "transparent",
            color: isPro ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.22)",
            cursor: isPro ? "pointer" : "not-allowed",
          }}
          title={isPro
            ? (t.composite?.custom_title || "Configura pesos personalizados")
            : (t.composite?.custom_locked || "Personalización disponible en RE₿EL PRO")}
        >
          {t.composite?.custom_short || "CUST"}
          {!isPro && <span aria-hidden style={{ marginLeft: 2 }}>🔒</span>}
        </button>
      </div>

      {/* Tooltip / breakdown */}
      {tooltipOpen && (
        <div
          id="composite-signal-tooltip"
          role="tooltip"
          data-testid="composite-signal-tooltip"
          className="absolute z-50 rounded-lg border border-white/10 bg-[#0a0a0a] p-3 shadow-xl"
          style={{ top: "calc(100% + 6px)", left: 0, minWidth: 260 }}
        >
          <div className="mb-2 flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: style.color }}>
              {verdictLabel}
              <span className="ml-2 text-white/60">
                {snapshot.score > 0 ? `+${snapshot.score}` : snapshot.score}
              </span>
            </p>
            {!isPro && (
              <span className="text-[8px] uppercase tracking-wider text-amber-500/80" data-testid="composite-delay-badge">
                {t.composite?.delayed || "DIFERIDO 5 MIN"}
              </span>
            )}
          </div>
          <p className="mb-2 text-[9px] uppercase tracking-wider text-white/45">
            {t.composite?.breakdown_title || "Contribuyentes activos"}
          </p>
          {snapshot.entries.length === 0 ? (
            <p className="text-[10px] text-white/45">
              {t.composite?.no_signals || "Sin señales activas — activa toggles del chart."}
            </p>
          ) : (
            <ul className="space-y-1" data-testid="composite-breakdown">
              {snapshot.entries.map((e) => {
                const isBull = e.score > 0;
                const dotColor = isBull ? "#10b981" : e.score < 0 ? "#ef4444" : "#94a3b8";
                const label = t.composite?.contrib?.[e.key] || e.key;
                return (
                  <li key={e.key} className="flex items-center justify-between text-[10px]">
                    <span className="flex items-center gap-1.5">
                      <span
                        style={{
                          display: "inline-block", width: 6, height: 6, borderRadius: 3,
                          background: dotColor,
                        }}
                      />
                      <span className="text-white/80">{label}</span>
                    </span>
                    <span className="text-white/60">
                      {e.score > 0 ? `+${Math.round(e.score)}` : Math.round(e.score)}
                      <span className="ml-1 text-white/35">· w{e.weight}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          <p className="mt-2 border-t border-white/10 pt-2 text-[9px] leading-snug text-white/40">
            {t.composite?.disclaimer
              || "Score compuesto de indicadores activos. No es asesoramiento financiero."}
          </p>
        </div>
      )}
    </div>
  );
}
